/* eslint-disable max-lines-per-function */
import React, {FC, useEffect} from 'react';
import {AuthFormError, Button, FormError} from 'components/Basic';
import {
  FormNumberInput,
  FormSelect,
  FormTextInput,
} from 'components/Basic/Form/V2';
import {statesInUSA} from 'definitions/States';
import {RegisterOutsideUSASchema, RegisterSchema} from 'definitions/Yup';
import {userActions} from 'features/User';
import {useCookie, useEnhancedForm} from 'hooks';
import {
  DigitalPracticeProvider,
  RegisterFormData,
  RegisterOutsideUSAFormData,
  SliceStatus,
  StripePlanDurations,
  StripePlanNames,
} from 'interfaces';
import {Trans, useTranslation} from 'react-i18next';
import {useParams} from 'react-router';
import {rsStyleV1} from 'styles';

type Props = {
  containerUtilities?: string;
  referralCode: string | undefined;
  paymentPlan: StripePlanNames;
  paymentPlanDuration: StripePlanDurations;
  socialOnboardingExtraFlow?: string;
  referralSource: string;
  provider: DigitalPracticeProvider;
  providerLoadingStatus: SliceStatus;
};

const defaultValues = {
  fullName: '',
  email: '',
  stateOfResidence: '',
  phoneNumber: '',
  password: '',
  termsOfService: false,
};

type OptionType = {label: string; value: string};
const renderStates = (): OptionType[] =>
  statesInUSA.map(state => ({value: state, label: state}));

export const OutsideWellniteRegisterForm: FC<Props> = ({
  containerUtilities = 'w-full',
  paymentPlan,
  paymentPlanDuration,
  socialOnboardingExtraFlow,
  provider,
  providerLoadingStatus,
}) => {
  const {providerId} = useParams<{providerId: string}>();
  const {referralSource} = useCookie();

  const {t} = useTranslation();

  const {
    formMethods: {
      register,
      formState: {errors},
      control,
      setValue,
    },
    isLoading,
    apiErrorMsg,
    onSubmit,
  } = useEnhancedForm<
    RegisterOutsideUSAFormData | RegisterFormData,
    {
      paymentPlan: StripePlanNames;
      duration: StripePlanDurations;
      socialOnboardingExtraFlow?: string;
      providerId: string;
      referralSource?: string;
    }
  >({
    defaultValues,
    schema:
      provider?.countryOfResidence?.code === 'US'
        ? RegisterSchema
        : RegisterOutsideUSASchema,
    slice: 'user',
    action: userActions.outSideWellniteRegister,
    extraSubmitData: {
      referralSource,
      paymentPlan,
      duration: paymentPlanDuration,
      socialOnboardingExtraFlow,
      providerId,
    },
  });

  const errorMsg = Object.keys(errors).filter(e => e !== 'termsOfService')
    .length
    ? `You entered an incorrect ${Object.keys(errors)
        .filter(e => e !== 'termsOfService')
        .join(', ')} or missing.`
    : apiErrorMsg;
  const isPending = isLoading === SliceStatus.pending;
  useEffect(() => {
    if (provider) {
      setValue('countryOfResidence', provider?.countryOfResidence);
    }
  }, [provider]);

  return (
    <section className={`${containerUtilities}`}>
      <AuthFormError
        id="signup-error"
        error={errorMsg}
        showSignUpLink={false}
      />

      <form onSubmit={onSubmit}>
        <FormTextInput
          id="fullName"
          type="text"
          label={t('name')}
          isRequired={true}
          classes="mb-5 pt-0"
          labelClasses="text-xs leading-4 mt-4"
          inputClasses="font-medium text-sm leading-4 pl-3 h-12"
          control={control}
        />

        <FormNumberInput
          id="phoneNumber"
          labelClasses="text-xs leading-4 mt-4"
          isRequired={true}
          allowEmptyFormatting
          classes="mb-5 pt-0"
          label={t('phone')}
          inputClasses="font-medium text-sm leading-4 pl-3"
          control={control}
        />

        {provider?.countryOfResidence?.code === 'US' ? (
          <FormSelect
            id="stateOfResidence"
            label={t('state')}
            control={control}
            options={renderStates()}
            extractValue={true}
            classes="pt-0"
            labelClasses="text-xs leading-4"
            styles={rsStyleV1}
            placeholder="Select State"
          />
        ) : null}

        <FormTextInput
          id="email"
          type="email"
          label={t('email')}
          labelClasses="text-xs leading-4 mt-4"
          classes="mb-5 pt-0"
          inputClasses="font-medium text-sm leading-4 pl-3 h-12"
          control={control}
        />

        <FormTextInput
          id="password"
          type="password"
          label={t('password')}
          labelClasses="text-xs leading-4 mt-4"
          classes="mb-5 pt-0"
          inputClasses="font-medium text-sm leading-4 pl-3 h-12"
          control={control}
        />

        <article className="flex items-center px-2">
          <input
            id="termsOfService"
            type="checkbox"
            aria-label="termsOfService"
            {...register('termsOfService')}
            className="form-checkbox text-blue-600 transition duration-150 ease-in-out"
          />
          <label
            htmlFor="termsOfService"
            className="pl-1 md:pl-3 block text-xs leading-snug text-black"
          >
            <Trans
              i18nKey="consent"
              components={[
                <a
                  target="_blank"
                  title="Telehealth"
                  key="telehealth"
                  rel="noopener noreferrer"
                  className="text-blue-600"
                  href="https://www.wellnite.com/teleconsent"
                ></a>,
                <a
                  target="_blank"
                  title="Wellnite terms"
                  key="terms"
                  rel="noopener noreferrer"
                  className="text-blue-600"
                  href="https://www.wellnite.com/terms"
                ></a>,
              ]}
            ></Trans>
          </label>
        </article>
        <section className="mb-5 px-2">
          <FormError
            id="terms-error"
            error={
              errors.termsOfService && t(`${errors.termsOfService?.message}`)
            }
          />
        </section>
        <div className="w-full flex items-center justify-center">
          <article className="mx-auto">
            <Trans
              i18nKey="signup"
              components={[
                <Button
                  type="submit"
                  borderColor="transparent"
                  borderRadius="full"
                  key="submit"
                  className="px-8 py-2.5 mb-4"
                  isSubmitting={isPending}
                  disabled={providerLoadingStatus === SliceStatus.pending}
                ></Button>,
              ]}
            ></Trans>
          </article>
        </div>
      </form>
    </section>
  );
};
